import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, Inject, input, OnDestroy, OnInit, output, SecurityContext, signal, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonCloseDirective, ButtonDirective } from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { Conference } from 'calendar';
import { ConferenceMedia } from 'media';
import { addQueryParam, CrossFrameBridgeFactory, CrossFrameObservableBridge, ENVIRONMENT, getBaseUrl, Logger, Platform, SpinnerComponent } from 'shared';
import { Signature, ZoomService } from './zoom.service';

type ZoomContext = Signature & {
  meetingNumber: string,
  password?: string,
  userName: string,
  userEmail: string,
  leaveUrl?: string,
  tk?: string,
  zak?: string // only for host starting meeting on behalf of someone
}

@Component({
  selector: 'conference-zoom',
  templateUrl: './conference-zoom.component.html',
  styleUrls: ['./conference-zoom.component.scss'],
  imports: [ButtonDirective, SpinnerComponent, NgIf, SpinnerComponent, ButtonCloseDirective],
  animations: [
    trigger('fadeVisibility', [
      state('visible', style({ opacity: 1 })),
      state('hidden', style({ opacity: 0 })),
      transition('visible <=> hidden', animate('300ms ease-in-out')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConferenceZoomComponent implements OnInit, OnDestroy {

  // media = input.required<ConferenceMedia | undefined>();
  
  media = input<ConferenceMedia | undefined>();
  conference = input<Conference | undefined>();

  conference$ = computed<Conference | undefined>(() => {
    return this.conference() || this.media()?.properties?.['conference'];
  });
  
  action = output<any>();
  onClose = output<void>();

  iframes = viewChildren<ElementRef<HTMLIFrameElement>>('iframe');
  iframeElement: HTMLIFrameElement | undefined = undefined;
  iframeUrl: SafeResourceUrl | null = null;
  url = `${this.environment.serverUrl}/conference/zoom/viewer`;

  loading = signal(true);
  error = signal<string | null>(null);
  showOverlay = signal(true);
  overlayState = computed(() => this.loading() || this.error() ? 'visible' : 'hidden');
  iframeState = computed(() => this.overlayState() == 'hidden' ? 'visible' : 'hidden');

  joined = signal(false);

  private iframeTimeoutMs = 30000;
  private iframeTimeout: any;

  private retry = false;

  private context: Signature | null = null;

  private bridge: CrossFrameObservableBridge | null = null;

  protected logger = new Logger('ConferenceZoomComponent');

  constructor(
    private sanitizer: DomSanitizer,
    private zoomService: ZoomService,
    private bridgeFactory: CrossFrameBridgeFactory,
    private authService: AuthService,
    private platform: Platform,
    private destroyRef: DestroyRef,
    @Inject(ENVIRONMENT) private environment: any
  ) {
    effect(() => {
      this.iframeElement = this.iframes()[0]?.nativeElement;
      if (this.iframeElement) {
        this.initializeIframe();
      } else if (this.retry) {
        this.retry = false;
        setTimeout(() => this.initialize());
      }
    });
  }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }
    this.bridge?.destroy();
  }

  onOverlayAnimationDone(event: any) {
    if (event.toState === 'hidden') {
      this.showOverlay.set(false);
    }
  }

  onRetry(): void {
    this.retry = true;
    this.error.set(null);
    this.loading.set(true);
    this.iframeUrl = null;
  }

  onCancel(): void {
    this.action.emit('back');
  }

  private initialize() {
    let url: string | null = `${this.url}`;
    if (!url.endsWith('/')) {
      url += '/';
    }
    url = addQueryParam(url, 'ngsw-bypass');
    // url += '?ngsw-bypass';
    url = this.sanitizer.sanitize(SecurityContext.URL, url);
    if (url) {
      this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      this.handleError(new Error('Invalid room URL'));
    }
  }

  private initializeIframe(): void {
    const bridge = this.getCrossFrameBridge(this.iframeElement!);
    if (!bridge) {
      this.logger.warn('Failed to initialize iframe');
      return;
    }
    bridge.onRequest("getContext", async (payload) => {
      this.logger.debug('MESSAGE > getContext');
      const getContext = async () => {
        const conference = this.conference$();
        if (conference) {
          const signature: Signature = await this.zoomService.getSignature(conference);
          if (signature) {
            const user = this.authService.getUser();
            const context: ZoomContext = {
              ...signature,
              meetingNumber: conference.properties?.room_name,
              password: conference.properties?.password,
              userName: user?.full_name ?? '',
              userEmail: user?.email ?? '',
              // tk: registrantToken,
              zak: '', // only for host starting meeting on behalf of someone
            }
            return context;
          } else {
            throw new Error('Empty or incomplete signature received from server')
          }
        } else {
          throw new Error('Conference is not initialized');
        }
      }
      try {
        this.context = await getContext();
        return {
          ...this.context,
          origin: window.location.origin,
          platforms: this.platform.platforms()
        };
      } catch (error) {
        this.logger.error('Failed to retrieve chat context', error);
        throw error;
      }
    });

    bridge.onEvent$("navigateBack")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.action.emit('back');
      });

    bridge.onEvent$("loaded")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.loading.set(false);
      });

    bridge.onEvent$("userJoin")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.logger.debug('userJoin', event);
        this.joined.set(true);
      });

    bridge.onEvent$("userLeave")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        // reasonCode Return the reason the current user left.
        // const reasonCode = {
        //   OTHER: 0, // Other reason.
        //   HOST_ENDED_MEETING: 1, // Host ended the meeting.
        //   SELF_LEAVE_FROM_IN_MEETING: 2, // User (self) left from being in the meeting.
        //   SELF_LEAVE_FROM_WAITING_ROOM: 3, // User (self) left from the waiting room.
        //   SELF_LEAVE_FROM_WAITING_FOR_HOST_START: 4, // User (self) left from waiting for host to start the meeting.
        //   MEETING_TRANSFER: 5, // The meeting was transferred to another end to open.
        //   KICK_OUT_FROM_MEETING: 6, // Removed from meeting by host or co-host.
        //   KICK_OUT_FROM_WAITING_ROOM: 7, // Removed from waiting room by host or 
        //     co-host.
        //   LEAVE_FROM_DISCLAIMER: 8, // User click cancel in disclaimer dialog 
        // };
        // see https://marketplacefront.zoom.us/sdk/meeting/web/functions/ZoomMtg.inMeetingServiceListener.html


        // {reason: 'You are leave meeting by yourself', reasonCode: 2}
        // {reason: 'You are removed by host/co-host from meeting', reasonCode: 6}
        // {reason: 'You are leave meeting by yourself', reasonCode: 0}

        this.logger.debug('userLeave', event);
        // const reasonCode = event.payload?.reasonCode
        // if ([0,2,6].includes(reasonCode)) {
          this.joined.set(false);
          this.close();
        // }
      });

    bridge.onEvent$("userUpdate")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.logger.debug('userUpdate', event);
      });

    this.iframeElement?.addEventListener('load', () => {
      clearTimeout(this.iframeTimeout);
      // this.loading = false;
    }, { once: true });

    this.iframeTimeout = setTimeout(() => {
      this.handleError(new Error('Iframe failed to load within timeout'));
    }, this.iframeTimeoutMs);
  }

  protected getCrossFrameBridge(iframe: HTMLIFrameElement): CrossFrameObservableBridge | null {
    if (!this.bridge) {
      const targetOrigin = getBaseUrl(this.url);
      if (targetOrigin) {
        this.bridge = this.bridgeFactory.create({
          targetWindow: iframe.contentWindow!,
          targetOrigin: targetOrigin,
          allowedOrigins: [targetOrigin]
        })
      } else {
        this.logger.debug(`Invalid URL: ${this.url} `)
      }
    }
    return this.bridge;
  }

  private handleError(error: Error): void {
    this.loading.set(false);
    this.error.set('Failed to load Zoom meeting. Please reload or try again later.');
    this.logger.error(error.message);
  }

  close(event?:Event) {
    event?.preventDefault();
    this.onClose.emit();
  }
}